import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { PageHeader } from '../../components/layout/PageHeader';
import { useCurrentUser } from '../auth';
import { listAccounts } from '../accounts/api';
import { ApiError } from '../../lib/api';
import type { Account } from '../../types/account';
import type { AudienceScope } from '../../types/broadcast';
import type { Survey } from '../../types/survey';
import { listDeletedSurveys, restoreSurvey } from './api';

// Phase 6B F6 — admin recovery view at /surveys/deleted. Lists soft-deleted
// surveys (deletedAt / deletedBy) with a per-row Restore that clears the
// soft-delete and returns the survey to the main list. Responses are preserved
// throughout (only sent surveys soft-delete; drafts hard-delete).

const STATUS_LABEL: Record<Survey['status'], string> = {
  draft: 'Draft',
  sending: 'Sending',
  open: 'Open',
  closed: 'Closed',
  cancelled: 'Cancelled',
};
const STATUS_VARIANT: Record<Survey['status'], BadgeVariant> = {
  draft: 'info',
  sending: 'warning',
  open: 'success',
  closed: 'neutral',
  cancelled: 'danger',
};

function brandLabel(scope: AudienceScope, accounts: Account[]): string {
  if (scope.brandSelection === 'all') return 'All brands';
  const ids = scope.brandSelection.brandAccountIds;
  if (ids.length === 0) return '—';
  const names = ids
    .map((id) => accounts.find((a) => a.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  return names.length > 0 ? names.join(', ') : '—';
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function DeletedSurveysPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const deleted = useQuery({
    queryKey: ['surveys', 'deleted'],
    queryFn: listDeletedSurveys,
    enabled: me.role === 'admin',
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const acc = accounts.data ?? [];

  const restore = useMutation({
    mutationFn: (s: Survey) => restoreSurvey(s.id),
    onMutate: (s) => {
      setRestoringId(s.id);
      setError(null);
    },
    // Prefix-invalidate ['surveys'] so BOTH this deleted list and the main list
    // refetch — the restored survey leaves here and reappears there.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Restore failed.'),
    onSettled: () => setRestoringId(null),
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view deleted surveys.</p>;
  }

  return (
    <div>
      <PageHeader
        title="Deleted surveys"
        description="Soft-deleted surveys (sent surveys are never hard-deleted). Restoring returns one to the main list; recipient and response data were preserved throughout."
        backHref="/surveys"
      />

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {deleted.isLoading ? (
        <CenteredSpinner />
      ) : deleted.error ? (
        <p className="text-sm text-red-600">Couldn't load deleted surveys.</p>
      ) : deleted.data && deleted.data.length > 0 ? (
        <div className="overflow-hidden rounded-md border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="px-4 py-2 font-medium">Brand</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Responses</th>
                <th className="px-4 py-2 font-medium">Deleted</th>
                <th className="px-4 py-2 font-medium">Deleted by</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {deleted.data.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-medium text-slate-900">{s.title || 'Untitled survey'}</td>
                  <td className="px-4 py-2 text-slate-600">{brandLabel(s.audienceScope, acc)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{s.submittedCount}</td>
                  <td className="px-4 py-2 text-slate-600">{fmtDate(s.deletedAt)}</td>
                  <td className="px-4 py-2 text-slate-600">{s.deletedBy?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => restore.mutate(s)}
                      disabled={restoringId === s.id}
                      className="text-sm text-sky-700 underline hover:text-sky-900 disabled:opacity-50"
                    >
                      {restoringId === s.id ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-slate-600">No deleted surveys.</p>
      )}
    </div>
  );
}
